import React from "react";

export function PageHeader({ kicker, title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-6 mb-8 pb-6 border-b border-neutral-200">
      <div className="min-w-0 break-words">
        {kicker && (
          <div className="font-mono-kpi text-[10px] uppercase tracking-[0.25em] text-[#FF5A00] mb-2">
            // {kicker}
          </div>
        )}
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-none">
          {title}
        </h1>
        {subtitle && <p className="mt-2 text-sm text-neutral-500 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 max-w-full">{actions}</div>}
    </div>
  );
}

export function PageContainer({ children }) {
  return <div className="px-4 sm:px-8 py-8 lg:px-12 lg:py-10 max-w-[1400px] mx-auto">{children}</div>;
}

export function StatusBadge({ status, ...props }) {
  const map = {
    paid: { label: "Pagado", cls: "bg-[#00C853]/10 text-[#008C3A] border-[#00C853]/30" },
    pending: { label: "Pendiente", cls: "bg-[#FFC107]/10 text-[#B28C00] border-[#FFC107]/40" },
    overdue: { label: "Vencido", cls: "bg-[#D50000]/10 text-[#B71C1C] border-[#D50000]/30" },
    cancelled: { label: "Cancelado", cls: "bg-neutral-100 text-neutral-500 border-neutral-300" },
    scheduled: { label: "Programado", cls: "bg-[#2962FF]/10 text-[#1E40AF] border-[#2962FF]/30" },
    in_progress: { label: "En curso", cls: "bg-[#FF5A00]/10 text-[#C2410C] border-[#FF5A00]/30" },
    pending_review: { label: "En revisión", cls: "bg-amber-50 text-amber-800 border-amber-300" },
    completed: { label: "Completado", cls: "bg-[#00C853]/10 text-[#008C3A] border-[#00C853]/30" },
    draft: { label: "Borrador", cls: "bg-neutral-100 text-neutral-600 border-neutral-300" },
    sent: { label: "Enviada", cls: "bg-[#2962FF]/10 text-[#1E40AF] border-[#2962FF]/30" },
    approved: { label: "Aprobada", cls: "bg-[#00C853]/10 text-[#008C3A] border-[#00C853]/30" },
    rejected: { label: "Rechazada", cls: "bg-[#D50000]/10 text-[#B71C1C] border-[#D50000]/30" },
    converted: { label: "Convertida", cls: "bg-[#FF5A00]/10 text-[#C2410C] border-[#FF5A00]/30" },
    // Invoice states
    por_facturar: { label: "Por Facturar", cls: "bg-[#FFC107]/10 text-[#B28C00] border-[#FFC107]/40" },
    facturado: { label: "Facturado", cls: "bg-[#2962FF]/10 text-[#1E40AF] border-[#2962FF]/30" },
    pagado: { label: "Pagado", cls: "bg-[#00C853]/10 text-[#008C3A] border-[#00C853]/30" },
    vencido: { label: "Vencido", cls: "bg-[#D50000]/10 text-[#B71C1C] border-[#D50000]/30" },
  };
  const m = map[status] || { label: status, cls: "bg-neutral-100 text-neutral-600 border-neutral-300" };
  return (
    <span {...props} className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${m.cls}`}>
      {m.label}
    </span>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div className="border border-dashed border-neutral-300 bg-white py-16 px-6 text-center">
      <div className="font-display text-lg font-bold">{title}</div>
      {description && <div className="mt-2 text-sm text-neutral-500 max-w-md mx-auto">{description}</div>}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`bg-[#FF5A00] hover:bg-[#E65100] text-white px-5 py-2.5 font-mono-kpi text-xs uppercase tracking-[0.15em] transition-colors disabled:opacity-50 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`bg-white border border-neutral-300 hover:border-[#0A0A0A] text-[#0A0A0A] px-5 py-2.5 font-mono-kpi text-xs uppercase tracking-[0.15em] transition-colors ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button
      className={`bg-transparent hover:bg-neutral-100 text-[#0A0A0A] px-3 py-2 text-sm transition-colors ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-xs text-neutral-400 mt-1">{hint}</div>}
    </label>
  );
}

export const inputCls =
  "w-full px-3 py-2 border border-neutral-300 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/15 outline-none bg-white text-sm";
